import { evaluateGate, inputFromSwitch, ledFromOutput } from './LogicEvaluator';
import type { CircuitState, GateType } from './SignalState';

type Listener = (state: Readonly<CircuitState>) => void;

export class CircuitModel {
  private state: CircuitState;
  private listeners = new Set<Listener>();

  constructor() {
    this.state = this.calculate({
      gateType: 'AND',
      switchK1: true,
      switchK2: true,
      vccConnected: true,
      gndConnected: true,
      wiringComplete: true
    });
  }

  get snapshot(): Readonly<CircuitState> {
    return { ...this.state };
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot);
    return () => this.listeners.delete(listener);
  }

  setGateType(gateType: GateType): void {
    this.update({ gateType });
  }

  toggleSwitch(key: 'K1' | 'K2'): void {
    if (key === 'K1') this.update({ switchK1: !this.state.switchK1 });
    else this.update({ switchK2: !this.state.switchK2 });
  }

  setInputs(a: 0 | 1, b: 0 | 1): void {
    this.update({ switchK1: a === 0, switchK2: b === 0 });
  }

  setWiring(complete: boolean): void {
    this.update({
      wiringComplete: complete,
      vccConnected: complete,
      gndConnected: complete
    });
  }

  private update(patch: Partial<CircuitState>): void {
    this.state = this.calculate({ ...this.state, ...patch });
    this.listeners.forEach((listener) => listener(this.snapshot));
  }

  private calculate(base: Pick<CircuitState, 'gateType' | 'switchK1' | 'switchK2' | 'vccConnected' | 'gndConnected' | 'wiringComplete'>): CircuitState {
    const inputA = inputFromSwitch(base.switchK1);
    const inputB = inputFromSwitch(base.switchK2);
    const powered = base.vccConnected && base.gndConnected && base.wiringComplete;
    const outputY = powered ? evaluateGate(base.gateType, inputA, inputB) : 0;
    const ledOn = powered && ledFromOutput(outputY);
    return {
      ...base,
      inputA,
      inputB,
      outputY,
      ledOn,
      currentPathActive: ledOn
    };
  }
}
